import { useState } from "react";
import { ChevronRight, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { CALC_CATEGORIES, getCategory } from "../utils/categories";
import { calculateItem, formatUSD, type CalcItem, type ItemResult } from "../utils/acvRcv";

interface HouseholdTableProps {
  items: CalcItem[];
  yearsForward: number;
  onChange: (items: CalcItem[]) => void;
}

export default function HouseholdTable({ items, yearsForward, onChange }: HouseholdTableProps) {
  const [editingId, setEditingId] = useState<string | null>(null);

  const update = (id: string, patch: Partial<CalcItem>) => {
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };
  const remove = (id: string) => {
    onChange(items.filter((it) => it.id !== id));
    setEditingId(null);
  };

  const editingItem = items.find((it) => it.id === editingId) ?? null;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header (desktop only) */}
      <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2.5 bg-muted/40 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border">
        <div className="col-span-3">Item</div>
        <div className="col-span-3">Category</div>
        <div className="col-span-1 text-right">RCV</div>
        <div className="col-span-1 text-center">Age</div>
        <div className="col-span-1 text-center">Qty</div>
        <div className="col-span-2 text-right">ACV / Gap</div>
        <div className="col-span-1" aria-hidden />
      </div>

      <div className="divide-y divide-border">
        {items.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No items yet — pick a preset above or add one below.
          </div>
        )}
        {items.map((item) => {
          const result = calculateItem(item, yearsForward);
          return (
            <div key={item.id}>
              {/* Mobile: tap-to-edit summary row */}
              <MobileRow item={item} result={result} onOpen={() => setEditingId(item.id)} />
              {/* Desktop: inline editable grid */}
              <DesktopRow item={item} result={result} onUpdate={update} onRemove={remove} />
            </div>
          );
        })}
      </div>

      {/* Mobile edit sheet */}
      <Sheet open={!!editingItem} onOpenChange={(open) => !open && setEditingId(null)}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[92vh] overflow-y-auto">
          {editingItem && (
            <EditItemSheet
              item={editingItem}
              onUpdate={(patch) => update(editingItem.id, patch)}
              onRemove={() => remove(editingItem.id)}
              onClose={() => setEditingId(null)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Mobile summary row ──────────────────────────────────────────────────────

function MobileRow({
  item,
  result,
  onOpen,
}: {
  item: CalcItem;
  result: ItemResult;
  onOpen: () => void;
}) {
  const cat = getCategory(item.categoryId);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="md:hidden w-full text-left px-3 py-2.5 active:bg-muted/40 transition-colors flex items-center gap-2"
      aria-label={`Edit ${item.name}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-medium text-[14px] truncate leading-tight">
            {item.name}
            {item.qty > 1 && <span className="text-muted-foreground font-normal"> ×{item.qty}</span>}
          </div>
          <div className="text-[13px] tabular-nums shrink-0 leading-tight">
            {formatUSD(result.rcvTotal)}
          </div>
        </div>
        <div className="flex items-baseline justify-between gap-2 mt-0.5">
          <div className="text-[11px] text-muted-foreground truncate leading-tight">
            {cat.label} · {item.ageYears}y
          </div>
          <div className="text-[11px] tabular-nums text-destructive shrink-0 leading-tight">
            −{formatUSD(result.gap)}
          </div>
        </div>
      </div>
      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
    </button>
  );
}

// ─── Desktop inline row (unchanged behavior) ─────────────────────────────────

interface DesktopRowProps {
  item: CalcItem;
  result: ItemResult;
  onUpdate: (id: string, patch: Partial<CalcItem>) => void;
  onRemove: (id: string) => void;
}

function DesktopRow({ item, result, onUpdate, onRemove }: DesktopRowProps) {
  const acvPct = result.rcvTotal > 0 ? (result.acvTotal / result.rcvTotal) * 100 : 0;

  return (
    <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-muted/30 transition-colors">
      <div className="col-span-3">
        <Input
          value={item.name}
          onChange={(e) => onUpdate(item.id, { name: e.target.value })}
          className="h-8 text-sm"
          aria-label="Item name"
        />
      </div>
      <div className="col-span-3">
        <Select value={item.categoryId} onValueChange={(v) => onUpdate(item.id, { categoryId: v })}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            {CALC_CATEGORIES.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-1">
        <Input
          type="number"
          inputMode="numeric"
          value={item.rcv || ""}
          onChange={(e) => onUpdate(item.id, { rcv: Number(e.target.value) || 0 })}
          className="h-8 text-sm text-right"
          aria-label="Replacement cost"
        />
      </div>
      <div className="col-span-1">
        <Input
          type="number"
          inputMode="numeric"
          value={item.ageYears}
          onChange={(e) => onUpdate(item.id, { ageYears: Math.max(0, Number(e.target.value) || 0) })}
          className="h-8 text-sm text-center"
          aria-label="Age in years"
        />
      </div>
      <div className="col-span-1">
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          value={item.qty}
          onChange={(e) => onUpdate(item.id, { qty: Math.max(1, Number(e.target.value) || 1) })}
          className="h-8 text-sm text-center"
          aria-label="Quantity"
        />
      </div>
      <div className="col-span-2 flex items-center gap-2">
        <div className="flex-1">
          <div
            className="h-2 rounded-full bg-destructive/30 overflow-hidden"
            role="img"
            aria-label={`${result.depreciationPct}% depreciated`}
          >
            <div
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${acvPct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5 tabular-nums">
            <span className="text-emerald-700">{formatUSD(result.acvTotal)}</span>
            <span className="text-destructive">−{formatUSD(result.gap)}</span>
          </div>
        </div>
      </div>
      <div className="col-span-1 flex justify-end">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={() => onRemove(item.id)}
          aria-label={`Remove ${item.name}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Mobile edit sheet ───────────────────────────────────────────────────────

function EditItemSheet({
  item,
  onUpdate,
  onRemove,
  onClose,
}: {
  item: CalcItem;
  onUpdate: (patch: Partial<CalcItem>) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const cat = getCategory(item.categoryId);

  return (
    <>
      <SheetHeader className="text-left">
        <SheetTitle className="font-editorial text-xl">Edit item</SheetTitle>
      </SheetHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-1.5">
          <Label htmlFor="edit-name" className="text-xs text-muted-foreground">Name</Label>
          <Input
            id="edit-name"
            value={item.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            className="h-12 text-base"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="edit-cat" className="text-xs text-muted-foreground">Category</Label>
          <Select value={item.categoryId} onValueChange={(v) => onUpdate({ categoryId: v })}>
            <SelectTrigger id="edit-cat" className="h-12 text-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[60vh]">
              {CALC_CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.id} className="py-3 text-base">
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[11px] text-muted-foreground">
            Lifespan ~{cat.lifespan}y · {cat.curve}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="edit-rcv" className="text-xs text-muted-foreground">Replacement cost ($)</Label>
          <Input
            id="edit-rcv"
            type="number"
            inputMode="decimal"
            value={item.rcv || ""}
            onChange={(e) => onUpdate({ rcv: Number(e.target.value) || 0 })}
            className="h-12 text-base"
            placeholder="0"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="edit-age" className="text-xs text-muted-foreground">Age (years)</Label>
            <Input
              id="edit-age"
              type="number"
              inputMode="numeric"
              value={item.ageYears}
              onChange={(e) => onUpdate({ ageYears: Math.max(0, Number(e.target.value) || 0) })}
              className="h-12 text-base"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-qty" className="text-xs text-muted-foreground">Quantity</Label>
            <Input
              id="edit-qty"
              type="number"
              inputMode="numeric"
              min={1}
              value={item.qty}
              onChange={(e) => onUpdate({ qty: Math.max(1, Number(e.target.value) || 1) })}
              className="h-12 text-base"
            />
          </div>
        </div>
      </div>

      <SheetFooter className="flex-row gap-2 sm:flex-row">
        <Button
          variant="outline"
          onClick={onRemove}
          className="flex-1 h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Remove
        </Button>
        <Button onClick={onClose} className="flex-1 h-12">
          Done
        </Button>
      </SheetFooter>
    </>
  );
}
